import React from "react";
import { addDays, format, isSameDay, startOfDay, startOfWeek } from "date-fns";
import { colorOf, fmtTime } from "@/lib/schApi";

const HOUR_PX = 44;
export const weekRange = (d) => [startOfWeek(d), addDays(startOfWeek(d), 7)];
export const dayRange = (d) => [startOfDay(d), addDays(startOfDay(d), 1)];

/** Side-by-side lanes for overlapping events within a day column. */
function layout(list) {
  const sorted = [...list].sort((a, b) => a.start_time.localeCompare(b.start_time));
  const lanes = [];
  return sorted.map((e) => {
    let lane = lanes.findIndex((end) => end <= e.start_time);
    if (lane === -1) { lane = lanes.length; lanes.push(e.end_time); } else lanes[lane] = e.end_time;
    return { e, lane };
  }).map((x, _, all) => ({ ...x, lanes: Math.max(1, lanes.length) }));
}

export default function TimeGridView({ anchor, days: count, events, onOpen, onSlot }) {
  const first = count === 7 ? startOfWeek(anchor) : startOfDay(anchor);
  const days = Array.from({ length: count }, (_, i) => addDays(first, i));
  return (
    <div data-testid={count === 7 ? "sch-week" : "sch-day"} className="flex-1 min-h-0 overflow-y-auto">
      <div className="grid sticky top-0 z-10 bg-[#0B1220] border-b border-white/10" style={{ gridTemplateColumns: `52px repeat(${count}, 1fr)` }}>
        <div />
        {days.map((d) => (
          <div key={d.toISOString()} className={`px-2 py-1.5 text-xs ${isSameDay(d, new Date()) ? "text-nex-gold" : "text-nex-silver"}`}>{format(d, count === 7 ? "EEE d" : "EEEE, MMM d")}</div>
        ))}
      </div>
      <div className="grid relative" style={{ gridTemplateColumns: `52px repeat(${count}, 1fr)`, height: HOUR_PX * 24 }}>
        <div className="relative">
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="absolute right-1 font-mono text-[9px] text-nex-muted" style={{ top: h * HOUR_PX - 5 }}>{h ? format(new Date(2000, 0, 1, h), "ha") : ""}</div>
          ))}
        </div>
        {days.map((d) => {
          const list = layout(events.filter((e) => isSameDay(new Date(e.start_time), d)));
          return (
            <div key={d.toISOString()} className="relative border-l border-white/10" data-testid={`sch-col-${format(d, "yyyy-MM-dd")}`}>
              {Array.from({ length: 48 }, (_, i) => (
                <button key={i} type="button" aria-label={`New event ${format(d, "MMM d")} ${format(new Date(2000, 0, 1, Math.floor(i / 2), (i % 2) * 30), "h:mm a")}`}
                  data-testid={`sch-slot-new-${format(d, "yyyy-MM-dd")}-${String(Math.floor(i / 2)).padStart(2, "0")}${i % 2 ? "30" : "00"}`}
                  onClick={() => onSlot?.(new Date(d.getFullYear(), d.getMonth(), d.getDate(), Math.floor(i / 2), (i % 2) * 30))}
                  className={`absolute inset-x-0 hover:bg-nex-gold/10 transition-colors ${i % 2 ? "" : "border-t border-white/5"}`} style={{ top: i * HOUR_PX / 2, height: HOUR_PX / 2 }} />
              ))}
              {list.map(({ e, lane, lanes }) => {
                const s = new Date(e.start_time);
                const mins = Math.max(20, (new Date(e.end_time) - s) / 60000);
                const c = colorOf(e);
                return (
                  <button key={e.id} type="button" onClick={() => onOpen(e)} data-testid={`sch-event-${e.id}`}
                    className={`absolute rounded-sm px-1.5 py-1 text-left overflow-hidden border-l-2 hover:brightness-125 transition ${e.status !== "active" ? "opacity-50" : ""}`}
                    style={{ top: (s.getHours() * 60 + s.getMinutes()) * HOUR_PX / 60, height: mins * HOUR_PX / 60 - 2,
                      left: `calc(${(lane / lanes) * 100}% + 2px)`, width: `calc(${100 / lanes}% - 4px)`, borderColor: c, background: `${c}30` }}>
                    <div className="text-[11px] font-medium text-white truncate">{e.has_conflicts ? "⚠ " : ""}{e.title}</div>
                    <div className="font-mono text-[9px] text-white/60 truncate">{fmtTime(e.start_time)} · {e.location}</div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
